// src/components/RevisionNoteEditor.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import revisionService from '../../services/RevisionService';

const NOTE_OPTIONS = ['Silly mistake', 'New topic for me', 'Clarify concepts'];

/**
 * mode:
 *   - 'full'    : dropdown + switch (default)
 *   - 'note'    : dropdown only
 *   - 'recheck' : switch only
 */
const RevisionNoteEditor = ({ attemptId, questionId, userRole, mode = 'full', revisionData = null }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteId, setNoteId] = useState(null);
  const [category, setCategory] = useState('');
  const [rechecked, setRechecked] = useState(false);
  const [comment, setComment] = useState('');

  /* ------------------------------------------------------------------ */
  /* Fetch existing note                                                */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (revisionData && Array.isArray(revisionData)) {
      // Use provided revision data
      const matching = revisionData.find((n) => n.question === questionId);
      if (matching) {
        setNoteId(matching.id);
        setCategory(matching.student_note_category || '');
        setRechecked(matching.rechecked);
        setComment(matching.teacher_comment || '');
      }
      setLoading(false);
    } else {
      // Fallback to API call if no revision data provided
      (async () => {
        try {
          const { data } = await revisionService.list({
            test_attempt: attemptId,
            question: questionId,
          });
          const listData = Array.isArray(data) ? data : data.results || [];
          const matching = listData.find((n) => n.question === questionId);
          if (matching) {
            setNoteId(matching.id);
            setCategory(matching.student_note_category || '');
            setRechecked(matching.rechecked);
            setComment(matching.teacher_comment || '');
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to load revision note', err);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [attemptId, questionId, revisionData]);

  /* ------------------------------------------------------------------ */
  /* Save helper                                                        */
  /* ------------------------------------------------------------------ */
  const save = async (payload) => {
    setSaving(true);
    try {
      if (noteId) {
        await revisionService.update(noteId, payload);
      } else {
        const { data } = await revisionService.create({
          test_attempt: attemptId,
          question: questionId,
          ...payload,
        });
        setNoteId(data.id);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Could not save revision note', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={18} />;

  /* ------------------------------------------------------------------ */
  /* Student controls                                                   */
  /* ------------------------------------------------------------------ */
  const NoteDropdown = () => (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel id={`reason-${questionId}`}>Mark</InputLabel>
      <Select
        labelId={`reason-${questionId}`}
        value={category}
        label="Mark"
        onChange={(e) => {
          setCategory(e.target.value);
          save({ student_note_category: e.target.value });
        }}
      >
        {NOTE_OPTIONS.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const RecheckSwitch = () => (
    <FormControlLabel
      sx={{ m: 0 }}
      label={<Typography variant="body2"></Typography>}
      labelPlacement="start"
      control={
        <Switch
          checked={rechecked}
          onChange={(e) => {
            setRechecked(e.target.checked);
            save({ rechecked: e.target.checked });
          }}
          size="small"
        />
      }
    />
  );

  const renderStudent = () => {
    if (mode === 'note') return <NoteDropdown />;
    if (mode === 'recheck') return <RecheckSwitch />;
    // full
    return (
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <NoteDropdown />
        <RecheckSwitch />
      </Stack>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <Box sx={{ mt: 1 }}>
      {userRole === 'STUDENT' ? (
        renderStudent()
      ) : (
        <TextField
          label="Teacher comment"
          value={comment}
          variant="outlined"
          multiline
          size="small"
          fullWidth
          inputProps={{ maxLength: 200 }}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => save({ teacher_comment: comment })}
          helperText={`${comment.length}/200`}
        />
      )}
      {saving && <CircularProgress size={16} sx={{ ml: 1 }} />}
    </Box>
  );
};

export default RevisionNoteEditor;